import { useState } from "react";
import { ChevronDown } from "lucide-react";

import type { Department } from "@/types/domain";
import {
  ActionBar,
  ActionButton,
  Chip,
  Eyebrow,
  ScreenLead,
  ScreenTitle,
  type Tone,
} from "./OnboardingShell";
import { departmentLabel, splitDepartments } from "./lineHelpers";

/**
 * La pregunta de zona de cada flujo. Lista vacía = todo el Perú, que es como la
 * línea guarda "sin filtro de zona".
 */
export function ZoneStep({
  tone,
  eyebrow,
  title,
  lead,
  nextLabel,
  nextLoading,
  departments,
  departmentIds,
  onChange,
  onNext,
  onBack,
}: {
  tone: Tone;
  eyebrow: string;
  title: string;
  lead: string;
  nextLabel: string;
  nextLoading?: boolean;
  departments: Department[];
  departmentIds: number[];
  onChange: (ids: number[]) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const { featured, others } = splitDepartments(departments);
  // Si ya eligió uno de los otros (al volver atrás), la lista arranca abierta.
  const [showOthers, setShowOthers] = useState(() =>
    others.some((d) => departmentIds.includes(d.department_id)),
  );

  function toggle(id: number) {
    onChange(
      departmentIds.includes(id)
        ? departmentIds.filter((existing) => existing !== id)
        : [...departmentIds, id],
    );
  }

  const chip = (department: Department) => (
    <Chip
      key={department.department_id}
      tone={tone}
      selected={departmentIds.includes(department.department_id)}
      onClick={() => toggle(department.department_id)}
    >
      {departmentLabel(department.department_name)}
    </Chip>
  );

  return (
    <div>
      <Eyebrow tone={tone}>{eyebrow}</Eyebrow>
      <ScreenTitle>{title}</ScreenTitle>
      <ScreenLead>{lead}</ScreenLead>

      <div className="mt-6 flex max-w-[680px] flex-wrap gap-2">
        {featured.map(chip)}
        <Chip
          tone={tone}
          selected={departmentIds.length === 0}
          onClick={() => onChange([])}
        >
          Todo el Perú
        </Chip>
      </div>

      {others.length > 0 && (
        <div className="mt-4 max-w-[680px]">
          <button
            type="button"
            onClick={() => setShowOthers((open) => !open)}
            className="inline-flex cursor-pointer items-center gap-1.5 text-[14px] font-semibold text-slate-600 hover:text-slate-900"
          >
            Otros departamentos
            <ChevronDown
              className={showOthers ? "size-4 rotate-180" : "size-4"}
              strokeWidth={2.4}
            />
          </button>
          {showOthers && (
            <div className="mt-3 flex flex-wrap gap-2">{others.map(chip)}</div>
          )}
        </div>
      )}

      <ActionBar onBack={onBack}>
        <ActionButton tone={tone} onClick={onNext} loading={nextLoading}>
          {nextLabel}
        </ActionButton>
      </ActionBar>
    </div>
  );
}
